import { Grid, Tab, Tabs, CircularProgress } from "@material-ui/core";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";
import ClinicalSupply from "./ClinicalSupply";
import CommercialSupply from "./CommercialSupply";
import "./ConfigProductDetails.css";

const ConfigProductDetails = () => {
  const [view, setView] = useState(()=>localStorage.getItem('selectedSupplyView') || null);
  const [productConfig, setProductConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSupplytype, setSelectedSupplytype] = useState(null);
  const [clinicalTotalRecords, setClinicalTotalRecords] = useState(0);
  const [commercialTotalRecords, setCommercialTotalRecords] = useState(0);
  const { vendorMosymphonyID } = useParams();
  const location = useLocation();
const vendorName = location.state?.vendorName || vendorMosymphonyID || "";
  // Backend may return the clinical supply type as either "Clinical Supply" or "Product Development".
  const isClinicalSupplyName = (name) =>
    name === "Clinical Supply" || name === "Product Development";

  // Helper to get supplyType object by name
  const getSupplyTypeByName = (name) =>
    productConfig.find((item) =>
      isClinicalSupplyName(name)
        ? isClinicalSupplyName(item.supplyTypeName)
        : item.supplyTypeName === name
    );

  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");
  
    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);

  useEffect(() => {
    let isMounted = true;
    // Fetch product configuration data when component mounts
    axios
      .get(
        `${apiBaseUrl}/fetchPlantSupplyTypes/true`
      )
      .then((res) => {
        if (res.data.status) {
          const supplyTypes = res.data.data.supplyTypeList;
          setProductConfig(supplyTypes);
          // Set initial view and selectedSupplytype based on available supply types
          if (supplyTypes.length > 0) {
            const clinicalSupply = supplyTypes.find(st => isClinicalSupplyName(st.supplyTypeName));
            const commercialSupply = supplyTypes.find(st => st.supplyTypeName === 'Commercial Supply');
            const initialClinicalSupplyType = clinicalSupply || supplyTypes[0];
            const initialCommercialSupplyType = commercialSupply || clinicalSupply || supplyTypes[0];
            // getting stored supply view 
            const savedView = localStorage.getItem("selectedSupplyView");
            const hasCommercial = Boolean(commercialSupply);
            const defaultView = savedView !== null ? savedView : (hasCommercial ? 'CommercialSupply' : 'ClinicalSupply');
            setView(defaultView);
            if (savedView === null) {
              localStorage.setItem('selectedSupplyView', defaultView);
            }
            if (defaultView === 'CommercialSupply') {
              setSelectedSupplytype(initialCommercialSupplyType);
            } else {
              setSelectedSupplytype(initialClinicalSupplyType);
            }
          }
        } else {
          setError(res.data.message || "Failed to fetch product configuration");
        }
      })
      .catch((err) => {
        console.error("Error fetching product configuration:", err);
        setError("Something went wrong. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });
      // Conditional localStorage cleanup based on URL
      return () => {
        isMounted = false;
        const urlContainsProduct = /product/i.test(window.location.href);
        if (!urlContainsProduct) {
          localStorage.removeItem("selectedSupplyView");
        }
      };
    }, []);

  useEffect(() => {
    // Fetch counts for both tabs on mount or when productConfig changes
    const fetchCounts = async () => {
      try {
        const clinical = getSupplyTypeByName("Clinical Supply");
        const commercial = getSupplyTypeByName("Commercial Supply");
        
        // Clinical Supply count
        if (clinical) {
          const clinicalRes = await axios.post(
            `${apiBaseUrl}/fetchAllProducts`,
            {
              searchField: vendorName ? "CMO" : "all",
              searchValue:vendorName || null,
              productModality: "all",
              productType: "all",
              status: "all",
              supplyTypeID: clinical.supplyTypeID,
              pageNumber: 1,
              pageSize: 1,
            }
          );
          if (clinicalRes.data && clinicalRes.data.status) {
            setClinicalTotalRecords(clinicalRes.data.totalNoOfRecords || 0);
          }
        }
        
        // Commercial Supply count
        if (commercial) {
          const commercialRes = await axios.post(
            `${apiBaseUrl}/fetchAllProducts`,
            {
              searchField:  vendorName ? "CMO" : "all",
              searchValue: vendorName || null,
              productModality: "all",
              productType: "all",
              status: "all",
              supplyTypeID: commercial.supplyTypeID,
              pageNumber: 1,
              pageSize: 1,
            }
          );
          if (commercialRes.data && commercialRes.data.status) {
            setCommercialTotalRecords(commercialRes.data.totalNoOfRecords || 0);
          }
        }
      } catch (e) {
        // Optionally handle error
      }
    };
    if (productConfig.length > 0) fetchCounts();
  }, [productConfig]);

  const handleTabChange = (event, newValue) => {
    setView(newValue);
    // Storing the supplyview value
    localStorage.setItem("selectedSupplyView", newValue);
    if (productConfig.length === 0) return;
    if (newValue === "ClinicalSupply") {
      const supplyType = getSupplyTypeByName("Clinical Supply");
      setSelectedSupplytype(supplyType);
    }
    if (newValue === "CommercialSupply") {
      const supplyType = getSupplyTypeByName("Commercial Supply");
      setSelectedSupplytype(supplyType);
    }
  };

  // Check if supply type exists in the response
  const hasClinicalSupply = productConfig.some(item => isClinicalSupplyName(item.supplyTypeName));
  const hasCommercialSupply = productConfig.some(item => item.supplyTypeName === "Commercial Supply");

  return (
    <div className="config-product-details-wrapper">
      {loading ? (
        <div
          className="loading-container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress style={{ color: "primary" }} thickness={7} />
        </div>
      ) : error ? (
        <div
          className="error-container"
          style={{
            color: "red",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <p>{error}</p>
        </div>
      ) : (
        
        <Grid
          style={{
            padding: "0 0 0 1rem",
          }}
        >
          
          <Tabs
            value={view}
            onChange={handleTabChange}
            indicatorColor="secondary"
            textColor="secondary"
            style={{ color: "black" }}
          >
            {hasClinicalSupply && (
              <Tab
                style={{ color: view === "ClinicalSupply" ? "#1890ff" : "black" }}
                label={`Product Development (${clinicalTotalRecords})`}
                value="ClinicalSupply"
              />
            )}
            {hasCommercialSupply && (
              <Tab
                style={{
                  color: view === "CommercialSupply" ? "#1890ff" : "black",
                }}
                label={`Commercial Supply (${commercialTotalRecords})`}
                value="CommercialSupply"
              />
            )}
          </Tabs>
          <Grid item xs={12}>
            {view === "ClinicalSupply" && selectedSupplytype && hasClinicalSupply && (
              <ClinicalSupply
                selectedSupplytype={selectedSupplytype}
                productConfig={productConfig}
                  searchField="CMO"
  searchValue={vendorName}
              />
            )}
            {view === "CommercialSupply" && selectedSupplytype && hasCommercialSupply && (
              <CommercialSupply
                selectedSupplytype={selectedSupplytype}
                productConfig={productConfig}
                  searchField="CMO"
  searchValue={vendorName}
                  
              />
            )}
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default ConfigProductDetails;
